import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { supabase } from '../lib/supabase';

interface SubscriptionCheckProps {
  children: React.ReactNode;
  redirectTo?: string;
}

const SubscriptionCheck: React.FC<SubscriptionCheckProps> = ({ 
  children, 
  redirectTo = '/subscription' 
}) => {
  const { hasActiveSubscription, loading: subscriptionLoading, subscription } = useSubscription();
  const { loading: authLoading, user } = useAuth();
  const [isTeamMember, setIsTeamMember] = useState<boolean | null>(null);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user || hasCheckedStatus) return;

      try {
        setIsCheckingSubscription(true);
        
        // First check if user is a team member
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();

        if (userError) {
          console.error('Error checking user role:', userError);
          setIsTeamMember(false);
        } else if (userData && userData.role) {
          console.log('SubscriptionCheck - User is a team member, bypassing subscription check');
          setIsTeamMember(true);
          setHasCheckedStatus(true);
          
          // Store the result in sessionStorage to prevent re-checking
          sessionStorage.setItem('user-team-status', JSON.stringify({
            userId: user.id,
            isTeamMember: true,
            timestamp: Date.now()
          }));
          
          // Team members don't need subscriptions, so we don't need to set hasActiveSubscription
          return;
        } else {
          setIsTeamMember(false);
        }

        // Only check subscription if user is not a team member
        if (!isTeamMember) {
          console.log('SubscriptionCheck - User is not a team member, checking subscription status');
          const { data: subscriptionData, error: subscriptionError } = await supabase
            .from('user_subscriptions')
            .select('status')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .single();

          if (subscriptionError) {
            console.error('Error checking subscription:', subscriptionError);
            // hasActiveSubscription will be handled by the useSubscription hook
          } else {
            // hasActiveSubscription will be handled by the useSubscription hook
            console.log('Subscription status checked:', !!subscriptionData);
          }
        }
        
        setHasCheckedStatus(true);
        
        // Store the result in sessionStorage to prevent re-checking
        sessionStorage.setItem('user-team-status', JSON.stringify({
          userId: user.id,
          isTeamMember: false,
          timestamp: Date.now()
        }));
        
      } catch (error) {
        console.error('Error in checkSubscription:', error);
        setHasCheckedStatus(true);
        // hasActiveSubscription will be handled by the useSubscription hook
      } finally {
        setIsCheckingSubscription(false);
      }
    };

    // Check if we have a cached result in sessionStorage
    const cachedStatus = sessionStorage.getItem('user-team-status');
    if (cachedStatus && user) {
      try {
        const parsed = JSON.parse(cachedStatus);
        // Use cached result if it's for the same user and less than 5 minutes old
        if (parsed.userId === user.id && (Date.now() - parsed.timestamp) < 5 * 60 * 1000) {
          console.log('SubscriptionCheck - Using cached team status:', parsed.isTeamMember);
          setIsTeamMember(parsed.isTeamMember);
          setHasCheckedStatus(true);
          setIsCheckingSubscription(false);
          return;
        }
      } catch (e) {
        console.error('Error parsing cached status:', e);
        sessionStorage.removeItem('user-team-status');
      }
    }

    // Only check if we haven't checked before and have a user
    if (user && !hasCheckedStatus) {
      checkSubscription();
    }
  }, [user, hasCheckedStatus]);

  console.log('SubscriptionCheck - authLoading:', authLoading, 'user:', user?.id, 'subscriptionLoading:', subscriptionLoading, 'hasActiveSubscription:', hasActiveSubscription, 'subscriptionStatus:', subscription?.status);

  // Show loading if auth is still loading
  if (authLoading) {
    console.log('SubscriptionCheck - Auth is loading, showing loading screen');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading authentication...</p>
        </div>
      </div>
    );
  }

  // If user is not authenticated, redirect to login
  if (!user) {
    console.log('SubscriptionCheck - No user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Show loading if checking team member status
  if (isCheckingSubscription) {
    console.log('SubscriptionCheck - Checking subscription status, showing loading screen');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Checking your subscription...</p>
        </div>
      </div>
    );
  }

  // If user is a team member, allow access without subscription check
  if (isTeamMember) {
    console.log('SubscriptionCheck - User is a team member, allowing access');
    return <>{children}</>;
  }

  // Show loading if subscription is still being checked
  if (subscriptionLoading) {
    console.log('SubscriptionCheck - Subscription is loading, showing loading screen');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Checking subscription...</p>
        </div>
      </div>
    );
  }

  // Handle different subscription states
  if (subscription) {
    switch (subscription.status) {
      case 'active':
        console.log('SubscriptionCheck - User has active subscription, rendering children');
        return <>{children}</>;
        
      case 'past_due':
        console.log('SubscriptionCheck - Subscription is past due, allowing access with warning');
        return <>{children}</>;
        
      case 'unpaid':
        console.log('SubscriptionCheck - Subscription is unpaid, redirecting to subscription page');
        return <Navigate to="/subscription" replace />;
        
      case 'canceled':
        console.log('SubscriptionCheck - Subscription is canceled, redirecting to subscription page');
        return <Navigate to="/subscription" replace />;
        
      default:
        console.log('SubscriptionCheck - Unknown subscription status, redirecting to subscription page');
        return <Navigate to="/subscription" replace />;
    }
  }

  // If user is authenticated but has no active subscription, redirect to subscription page
  if (!hasActiveSubscription) {
    console.log('SubscriptionCheck - No active subscription, redirecting to subscription page');
    return <Navigate to="/subscription" replace />;
  }

  console.log('SubscriptionCheck - User authenticated and has active subscription, rendering children');
  return <>{children}</>;
};

export default SubscriptionCheck; 